import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Grid,
  Stack,
  pn,
  useElementBreakpoints
} from "@compass/core";
import { CustomFilters, DatePicker, Select } from "components";
import {
  ContributorMatchFilter as ContributorMatchFilterModel,
  ContributorMatchFilterResource
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const ContributorMatchFilter = (props: {
  filter: ContributorMatchFilterModel;
  setFilter: (value: ContributorMatchFilterModel) => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<ContributorMatchFilterResource>({});
  const properties = resource.properties || [];

  useEffect(() => {
    ApiService.contributorMatches.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const dateFromField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateFrom}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          )
        }
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateTo}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          )
        }
      />
    );
  };

  const contributorField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("contributor");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={props.filter.contributor}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.contributor = value;
              draft.contributorId = value?.id;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.contributors(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

   const serviceField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("serviceId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={resource.services?.find((x) => x.id === props.filter.serviceId)}
        options={resource.services}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.serviceId = value?.id;
            })
          )
        }
      />
    );
  };

  const isMatchField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isMatch)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.isMatch = value?.id;
            })
          )
        }
      />
    );
  };

  const hasFileField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("hasFile");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.hasFile)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.hasFile = value?.id;
            })
          )
        }
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<ContributorMatchFilterModel>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={resource.reviews?.find((x) => x.id === props.filter.reviewId)}
        options={resource.reviews}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.reviewId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="lg">
      <CustomFilters
        filters={props.filter.customFilters}
        setFilters={(filters) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.customFilters = filters;
            })
          )
        }
        properties={resource.customFilterProperties}
      />

      <Box>
        <Grid container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {dateFromField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {dateToField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {contributorField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {serviceField()}
         </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {isMatchField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {hasFileField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {reviewField()}
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
